/**
 * @description
 * 特定のアプリケーションでの特別対応処理をまとめたもの
 */

import { Maybe, PostDetailQuery } from '@/clients/utoniq-core/schema'
import { config } from '@/config'

/**
 * @description
 * OAuthクライアント制限のため、RESERVED_CLIENT_IDが取得できないアプリはTwitter認証を非表示にする
 * https://app.clickup.com/t/864egmuz3
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideTwitterAuth = () => {
  const TARGET_APPLICATION_KEYS = [
    'diica',
    'ndp',
    'kanonshizaki',
    'kowacomi',
    'rocomacoaco',
    'liella',
    'liyuu',
    'kiramune',
    'starrise',
    'waipoke',
    'playdeck',
    'aqours',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ポスト詳細のコメント一覧から、特定のユーザーのコメントを除外する
 * @param {Maybe<PostDetailQuery['post']>} post ポスト詳細のデータ
 * @returns {Maybe<PostDetailQuery['post']>} コメントフィルタリング後のポスト詳細のデータ
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=2508-56289&t=4X9KSOYM0lNd7t3M-1
 */
export const individualFilterBannedUserPostComment = (
  post: Maybe<PostDetailQuery['post']>
) => {
  if (!post?.replies) return post

  const TARGET_USER_IDS = ['mMdsjMWaciQIxHgPvG0nZBDiwGl1']

  return {
    ...post,
    replies: {
      ...post?.replies,
      edges: post?.replies?.edges?.filter(
        ({ node }) => !TARGET_USER_IDS.includes(node?.responder?.id ?? '')
      ),
    },
  }
}

/**
 * アイテム詳細ページの補足リンク領域にて、一部アプリでアイテム「情報」ページ導線の非表示フラグをONにする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 * cf SupplementLinkArea
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=3885-91376&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHiddenItemInfo = () => {
  const TARGET_APPLICATION_KEYS = ['omenz']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ユーザーが保有しているアイテムの発行数を非表示にする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 *
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=2628-55046&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHideItemIssuedNumber = () => {
  const TARGET_APPLICATION_KEYS = ['omenz']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 一部アプリのガチャページにおいて、在庫数・アイテム発行数を非表示にする
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 * cf
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-193363&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=180-22&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-193152&t=4X9KSOYM0lNd7t3M-1
 * https://www.figma.com/design/N0P35PTdaWpdnUY2ZFJbcA/App-Design?node-id=9916-194000&t=4X9KSOYM0lNd7t3M-1
 */
export const individualHideGachaRemainingItemCount = () => {
  const TARGET_APPLICATION_KEYS = [
    'liella',
    'kiramune',
    'liyuu',
    'omenz',
    'waipoke',
    'aqours',
    'machizero',
    'lana',
  ]

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アイテム編集画面でSKU毎の残数を表示するかどうかのフラグ
 * パフォーマンスのため、アサイーのみ表示する
 *
 * @return {Boolean} true: 表示する, false: 表示しない
 */
export const individualRemainingItemCountBySKU = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 氏名をカタカナで入力させるかどうかの判定
 * アサイーなど、現場で用いるアプリケーションでは氏名をカタカナで入力させる
 *
 * @return {Boolean} true: カタカナで入力させる, false: カタカナで入力させない
 */
export const individualUseFullNameKatakana = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * チュートリアルのスキップ導線を非表示にするかどうかの判定
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideTutorialSkip = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ストア詳細ページのシェアボタン・情報の導線を非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideStoreSupplementLink = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ストア詳細ページのアイテム情報の欄(種別・発行数・所有枚数)の表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideStoreItemInfo = () => {
  const TARGET_APPLICATION_KEYS = ['iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アーティストトップのグラデーションマスクの表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideArtistTopGradientMask = () => {
  const TARGET_APPLICATION_KEYS = ['yokohamameguri', 'iloveacai']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * アーティストトップの新着ポスト表示をOFFにする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideArtistNewPosts = () => {
  const TARGET_APPLICATION_KEYS = ['lana', 'standalone_stg']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * ウォレットを非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideWallet = () => {
  const TARGET_APPLICATION_KEYS = ['playdeck']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}

/**
 * 配送を非表示にする
 *
 * @return {Boolean} true: 非表示にする, false: 非表示にしない
 */
export const individualHideDelivery = () => {
  const TARGET_APPLICATION_KEYS = ['playdeck']

  return TARGET_APPLICATION_KEYS.includes(config.applicationKey)
}
